import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Lightbulb, Calendar, CreditCard, Users, MapPin, ArrowRight, Sparkles, Heart, Clock, Printer } from "@/components/icons";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Reveal } from "@/components/Reveal";
import { useParallax } from "@/hooks/useParallax";
import rodingHeaderImage from "@/assets/roding-header.jpg";
import rodingerGutscheineImage from "@/assets/rodinger-gutscheine.jpg";
import { InstagramFeed } from "@/components/InstagramFeed";
import { useSEO } from "@/hooks/useSEO";
import { supabase } from "@/integrations/supabase/client";

interface UpcomingEvent {
  id: string;
  title: string;
  description?: string;
  category: string;
  start_date: string;
  end_date?: string;
  location?: string;
}


// Board member images
import christianGoetzImage from "@/assets/vorstand/christian-goetz.jpg";
import elkeSchwarzfischerImage from "@/assets/vorstand/elke-schwarzfischer.jpg";
import michaelWenzlImage from "@/assets/vorstand/michael-wenzl.jpg";
import thomasAngermeierImage from "@/assets/vorstand/thomas-angermeier.jpg";
import hanneloreBergfeldImage from "@/assets/vorstand/hannelore-bergfeld.jpg";
import mariaFuchsImage from "@/assets/vorstand/maria-fuchs.jpg";
import kathrinSchegererImage from "@/assets/vorstand/kathrin-schegerer.jpg";
import piaGemollImage from "@/assets/vorstand/pia-gemoll.jpg";
import werbekreisImage from "@/assets/werbekreis-image.jpg";
import martinFuchsImage from "@/assets/vorstand/martin-fuchs.jpg";
import josefHeiglImage from "@/assets/fotograf-josef-heigl.webp";
  const Home = () => {
  useSEO({
    title: "Werbekreis Roding - Digitale Plattform für lokale Gemeinschaft",
    description: "Die digitale Plattform des Werbekreis Roding - Ideen einreichen, Events entdecken, Gutscheine nutzen und die lokale Gemeinschaft stärken.",
    keywords: "Roding, Werbekreis, Bayern, Oberpfalz, lokale Wirtschaft, Gutscheine, Events, Bürgerbeteiligung, Gemeinschaft",
    canonical: "https://werbekreis-roding.de"
  });

  const parallax = useParallax(0.22, 140);
  const [nextEvent, setNextEvent] = useState<UpcomingEvent | null>(null);
  const [loadingEvent, setLoadingEvent] = useState(true);

  useEffect(() => {
    const fetchNextEvent = async () => {
      try {
        const { data, error } = await supabase
          .from("public_events")
          .select("id, title, description, category, start_date, end_date, location")
          .gte("start_date", new Date().toISOString())
          .order("start_date", { ascending: true })
          .limit(1)
          .single();

        if (error && error.code !== "PGRST116") {
          console.error("Error fetching next event:", error);
        }
        setNextEvent(data || null);
      } catch (error) {
        console.error("Error fetching next event:", error);
      } finally {
        setLoadingEvent(false);
      }
    };

    fetchNextEvent();
  }, []);

  const formatEventDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      weekday: date.toLocaleDateString("de-DE", { weekday: "long" }),
      day: date.toLocaleDateString("de-DE", { day: "2-digit" }),
      month: date.toLocaleDateString("de-DE", { month: "long" }),
      year: date.toLocaleDateString("de-DE", { year: "numeric" }),
      time: date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    };
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + "…";
  };

  return <div className="space-y-16">
      {/* Hero Section */}
      <section className="relative py-20 min-h-[70vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <img
          src={rodingHeaderImage}
          alt=""
          aria-hidden="true"
          width={1600}
          height={1067}
          fetchPriority="high"
          decoding="async"
          style={{ transform: `translate3d(0, ${parallax}px, 0) scale(1.08)` }}
          className="absolute inset-0 h-full w-full object-cover will-change-transform motion-reduce:transform-none"
        />
        {/* Enhanced Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/30 to-black/40"></div>
        
        {/* Content */}
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in drop-shadow-2xl">
            Willkommen beim
            <span className="block md:inline md:ml-3 text-yellow-200 drop-shadow-2xl">Werbekreis Roding</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto animate-fade-in drop-shadow-lg text-white font-medium">
            Gemeinsam für ein lebendiges Roding – Ihre lokale Gemeinschaft für Handel, Events und Bürgerbeteiligung.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
            <Button asChild size="lg" className="btn-werbekreis shadow-2xl font-semibold">
              <Link to="/ideen">
                <Lightbulb className="mr-2 h-5 w-5" />
                Idee einreichen
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-2 border-white bg-white/10 backdrop-blur-sm text-white hover:bg-white hover:text-black shadow-2xl font-semibold">
              <Link to="/events">
                <Calendar className="mr-2 h-5 w-5" />
                Events entdecken
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Next Event Teaser */}
      {!loadingEvent && nextEvent && (
        <Reveal as="section" className="container mx-auto px-4">
          <Card className="card-werbekreis overflow-hidden border-l-4 border-l-primary interactive-glow">
            <div className="flex flex-col md:flex-row">
              {/* Date Block */}
              <div className="md:w-44 flex md:flex-col items-center justify-center p-6 bg-gradient-to-br from-primary via-primary-variant to-primary-glow text-white shadow-lg">
                <div className="text-center">
                  <div className="text-sm uppercase tracking-wide opacity-90">{formatEventDate(nextEvent.start_date).weekday}</div>
                  <div className="text-4xl md:text-5xl font-bold my-1 drop-shadow-md">{formatEventDate(nextEvent.start_date).day}</div>
                  <div className="text-lg font-medium">{formatEventDate(nextEvent.start_date).month}</div>
                  <div className="text-sm mt-1 opacity-90">{formatEventDate(nextEvent.start_date).year}</div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 p-6 md:p-8 flex flex-col justify-center">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    Nächstes Event
                  </span>
                  {nextEvent.category && nextEvent.category !== "general" && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground capitalize">
                      {nextEvent.category}
                    </span>
                  )}
                </div>

                <h2 className="text-2xl md:text-3xl font-bold mb-3">{nextEvent.title}</h2>

                {nextEvent.description && (
                  <p className="text-muted-foreground mb-4 max-w-3xl">
                    {truncateText(nextEvent.description, 220)}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-5">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-2 text-primary" />
                    <span>
                      {formatEventDate(nextEvent.start_date).time}
                      {nextEvent.end_date && ` – ${formatEventDate(nextEvent.end_date).time}`} Uhr
                    </span>
                  </div>
                  {nextEvent.location && (
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-primary" />
                      <span>{nextEvent.location}</span>
                    </div>
                  )}
                </div>

                <div>
                  <Button asChild className="btn-werbekreis">
                    <Link to="/events">
                      Alle Events entdecken
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </Reveal>
      )}


      {/* Main Feature Panels */}
      <Reveal as="section" className="grid md:grid-cols-2 gap-6">
        {/* Idee einreichen */}
        <Card className="card-werbekreis p-6 group hover:scale-105 transition-all duration-300">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center group-hover:animate-glow">
              <Lightbulb className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold">Idee einreichen</h3>
            <p className="text-muted-foreground">
              Haben Sie eine Idee für Roding? Teilen Sie sie mit der Gemeinschaft und 
              lassen Sie sie von anderen bewerten und unterstützen.
            </p>
            <Button asChild variant="outline" className="w-full group-hover:btn-werbekreis">
              <Link to="/ideen">
                Jetzt einreichen <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </Card>

        {/* Events entdecken */}
        <Card className="card-werbekreis p-6 group hover:scale-105 transition-all duration-300">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center group-hover:animate-glow">
              <Calendar className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold">Events entdecken</h3>
            <p className="text-muted-foreground">
              Verpassen Sie keine Veranstaltung mehr! Christkindlmarkt, verkaufsoffene 
              Sonntage und kulturelle Highlights in Roding.
            </p>
            <Button asChild variant="outline" className="w-full group-hover:btn-werbekreis">
              <Link to="/events">
                Termine ansehen <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </Card>
      </Reveal>

      {/* Gutschein Section - Full Width */}
      <Reveal as="section" className="py-8">
        <Card className="card-werbekreis p-0 group overflow-hidden bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 dark:from-green-950 dark:via-emerald-950 dark:to-teal-950">
          <div className="grid md:grid-cols-2 items-center">
            {/* Text Content */}
            <div className="p-8 lg:p-12 space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center group-hover:animate-glow shadow-lg">
                  <CreditCard className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-foreground">Rodinger Gutscheinkarte</h3>
                  <p className="text-lg text-primary font-medium">Lokale Wirtschaft stärken</p>
                </div>
              </div>
              
              <p className="text-lg text-muted-foreground leading-relaxed">
                Unterstützen Sie lokale Händler mit der Rodinger Gutscheinkarte. 
                Verfügbar in Werten von 10€, 25€ oder 50€ – ideal als Geschenk 
                und direkte Unterstützung für unsere Geschäfte vor Ort.
              </p>

              <div className="space-y-3">
                <Button asChild size="lg" className="btn-werbekreis w-full md:w-auto">
                  <Link to="/gutscheine">
                    <CreditCard className="mr-2 h-5 w-5" />
                    Alle Partner finden
                  </Link>
                </Button>
                <div className="flex flex-wrap gap-3">
                  <Button asChild variant="outline" size="sm">
                    <a 
                      href="https://aasys.de/roding/GS_Teilnehmerliste.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Printer className="mr-2 h-4 w-4" />
                      Liste ausdrucken
                    </a>
                  </Button>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Über 80 Akzeptanzstellen</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="relative h-64 md:h-full min-h-[300px]">
              <img 
                src={rodingerGutscheineImage} 
                alt="Rodinger Gutscheinkarten - Lokales Einkaufen unterstützen" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
          </div>
        </Card>
      </Reveal>

      {/* Community Features */}
      <Reveal as="section">
        {/* Mitglied werden */}
        <Card className="card-werbekreis p-6 group">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-indigo-500 to-blue-600 flex items-center justify-center group-hover:animate-glow">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold mb-2">Mitmachen</h3>
              <p className="text-muted-foreground mb-4">
                Werden Sie Teil unserer Gemeinschaft! Gestalten Sie die 
                Zukunft von Roding aktiv mit.
              </p>
              <Button asChild variant="outline" size="sm">
                <Link to="/mitmachen">
                  <Sparkles className="mr-2 h-4 w-4" />
                  Mitglied werden
                </Link>
              </Button>
            </div>
          </div>
        </Card>
      </Reveal>

      {/* Instagram Feed */}
      <InstagramFeed />

      {/* Statistics Section */}
      <Reveal as="section" className="card-werbekreis p-8">
        <h2 className="text-2xl font-bold text-center mb-8">Werbekreis Roding in Zahlen</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold gradient-text mb-2">80+</div>
            <div className="text-sm text-muted-foreground">Mitglieder</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold gradient-text mb-2">4+</div>
            <div className="text-sm text-muted-foreground">Events pro Jahr</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold gradient-text mb-2">60+</div>
            <div className="text-sm text-muted-foreground">Gutschein-Partner</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold gradient-text mb-2">1979</div>
            <div className="text-sm text-muted-foreground">Gegründet</div>
          </div>
        </div>
      </Reveal>

      {/* Board Members Section */}
      <Reveal as="section" className="card-werbekreis p-8">
        <h2 className="text-2xl font-bold text-center mb-8">Unser Vorstand</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* 1. Vorsitzender */}
          <Card className="p-6 text-center hover:shadow-lg transition-shadow group">
            <Avatar className="w-24 h-24 mx-auto mb-4">
              <AvatarImage src={christianGoetzImage} alt="Christian Götz" className="object-cover" />
              <AvatarFallback className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                <Users className="h-12 w-12" />
              </AvatarFallback>
            </Avatar>
            <h3 className="font-semibold text-primary mb-1">Christian Götz</h3>
            <p className="text-sm text-muted-foreground mb-2">1. Vorsitzender</p>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Schulstr. 16, 93426 Roding</p>
              <p>+49 151 59896405</p>
            </div>
          </Card>

          {/* 2. Vorsitzende */}
          <Card className="p-6 text-center hover:shadow-lg transition-shadow group">
            <Avatar className="w-24 h-24 mx-auto mb-4">
              <AvatarImage src={elkeSchwarzfischerImage} alt="Elke Schwarzfischer" className="object-cover" />
              <AvatarFallback className="bg-gradient-to-r from-pink-500 to-rose-600 text-white">
                <Users className="h-12 w-12" />
              </AvatarFallback>
            </Avatar>
            <h3 className="font-semibold text-primary mb-1">Elke Schwarzfischer</h3>
            <p className="text-sm text-muted-foreground mb-2">2. Vorsitzende</p>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Dr.-Held-Straße 1, 93426 Roding</p>
              <p>09461 2185</p>
            </div>
          </Card>

          {/* Kassier 1 */}
          <Card className="p-6 text-center hover:shadow-lg transition-shadow group">
            <Avatar className="w-24 h-24 mx-auto mb-4">
              <AvatarImage src={michaelWenzlImage} alt="Michael Wenzl" className="object-cover" />
              <AvatarFallback className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                <CreditCard className="h-12 w-12" />
              </AvatarFallback>
            </Avatar>
            <h3 className="font-semibold text-primary mb-1">Michael Wenzl</h3>
            <p className="text-sm text-muted-foreground mb-2">Kassier</p>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Regensburger Str. 18, 93426 Roding</p>
              <p>+49 9461 94150</p>
            </div>
          </Card>

          {/* Kassier 2 */}
          <Card className="p-6 text-center hover:shadow-lg transition-shadow group">
            <Avatar className="w-24 h-24 mx-auto mb-4">
              <AvatarImage src={martinFuchsImage} alt="Martin Fuchs" className="object-cover" />
              <AvatarFallback className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                <CreditCard className="h-12 w-12" />
              </AvatarFallback>
            </Avatar>
            <h3 className="font-semibold text-primary mb-1">Martin Fuchs</h3>
            <p className="text-sm text-muted-foreground mb-2">Kassier</p>
          </Card>

          {/* Schriftführer */}
          <Card className="p-6 text-center hover:shadow-lg transition-shadow group">
            <Avatar className="w-24 h-24 mx-auto mb-4">
              <AvatarImage src={thomasAngermeierImage} alt="Thomas Angermeier" className="object-cover" />
              <AvatarFallback className="bg-gradient-to-r from-indigo-500 to-blue-600 text-white">
                <Lightbulb className="h-12 w-12" />
              </AvatarFallback>
            </Avatar>
            <h3 className="font-semibold text-primary mb-1">Thomas Angermeier</h3>
            <p className="text-sm text-muted-foreground mb-2">Schriftführer</p>
          </Card>

          {/* Social Media Manager */}
          <Card className="p-6 text-center hover:shadow-lg transition-shadow group">
            <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden group-hover:animate-glow">
              <img src={werbekreisImage} alt="Stefan Obholz" className="w-24 h-24 object-cover" />
            </div>
            <h3 className="font-semibold text-primary mb-1">Stefan Obholz</h3>
            <p className="text-sm text-muted-foreground mb-2">Social Media Manager</p>
          </Card>

          {/* Ehrenmitglied & Pressesprecherin */}
          <Card className="p-6 text-center hover:shadow-lg transition-shadow group">
            <Avatar className="w-24 h-24 mx-auto mb-4">
              <AvatarImage src={hanneloreBergfeldImage} alt="Hannelore Bergfeld" className="object-cover" />
              <AvatarFallback className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white">
                <Heart className="h-12 w-12" />
              </AvatarFallback>
            </Avatar>
            <h3 className="font-semibold text-primary mb-1">Hannelore Bergfeld</h3>
            <p className="text-sm text-muted-foreground mb-2">Ehrenmitglied</p>
            <p className="text-xs text-muted-foreground">Pressesprecherin</p>
          </Card>

          {/* Ehrenmitglied & Beisitzerin */}
          <Card className="p-6 text-center hover:shadow-lg transition-shadow group">
            <Avatar className="w-24 h-24 mx-auto mb-4">
              <AvatarImage src={mariaFuchsImage} alt="Maria Fuchs" className="object-cover" />
              <AvatarFallback className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white">
                <Heart className="h-12 w-12" />
              </AvatarFallback>
            </Avatar>
            <h3 className="font-semibold text-primary mb-1">Maria Fuchs</h3>
            <p className="text-sm text-muted-foreground mb-2">Ehrenmitglied</p>
            <p className="text-xs text-muted-foreground">Beisitzerin</p>
          </Card>

          {/* Beisitzerin 1 */}
          <Card className="p-6 text-center hover:shadow-lg transition-shadow group">
            <Avatar className="w-24 h-24 mx-auto mb-4">
              <AvatarImage src={kathrinSchegererImage} alt="Kathrin Schegerer" className="object-cover" />
              <AvatarFallback className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
                <Users className="h-12 w-12" />
              </AvatarFallback>
            </Avatar>
            <h3 className="font-semibold text-primary mb-1">Kathrin Schegerer</h3>
            <p className="text-sm text-muted-foreground mb-2">Beisitzerin</p>
          </Card>

          {/* Beisitzerin 2 */}
          <Card className="p-6 text-center hover:shadow-lg transition-shadow group">
            <Avatar className="w-24 h-24 mx-auto mb-4">
              <AvatarImage src={piaGemollImage} alt="Pia Gemoll" className="object-cover" />
              <AvatarFallback className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
                <Users className="h-12 w-12" />
              </AvatarFallback>
            </Avatar>
            <h3 className="font-semibold text-primary mb-1">Pia Gemoll</h3>
            <p className="text-sm text-muted-foreground mb-2">Beisitzerin</p>
          </Card>
        </div>
      </Reveal>

      {/* Photography Credit */}
      <Reveal as="section" className="card-werbekreis p-6">
        <div className="text-center mb-4">
          <p className="text-sm text-muted-foreground mb-1">Fotografie</p>
          <h3 className="font-semibold text-xl">Josef Heigl</h3>
        </div>
        <div className="relative rounded-lg overflow-hidden">
          <img 
            src={josefHeiglImage} 
            alt="Fotograf Josef Heigl" 
            width={1200}
            height={800}
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-contain bg-muted"
          />
        </div>
      </Reveal>

      {/* Call to Action */}
      <Reveal as="section" className="text-center py-12">
        <div className="card-werbekreis p-8 bg-gradient-werbekreis text-foreground">
          <h2 className="text-3xl font-bold mb-4">Gestalten Sie Roding mit!</h2>
          <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
            Ihre Ideen, Ihre Meinung, Ihre Teilnahme – zusammen machen wir Roding 
            zu einem noch lebenswerteren Ort für alle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary">
              <Link to="/ideen">
                <Lightbulb className="mr-2 h-5 w-5" />
                Idee einreichen
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-foreground text-foreground hover:bg-foreground hover:text-background">
              <Link to="/mitmachen">
                <Users className="mr-2 h-5 w-5" />
                Mitglied werden
              </Link>
            </Button>
          </div>
        </div>
      </Reveal>
    </div>;
};
export default Home;